/**
 * AcademicMultiProfileView — Phase 72.A + 72.B orchestrator.
 *
 * Owns the profile-tabs + sub-tab selector. All real rendering work is
 * delegated to /components/academic/*Panel.jsx, keeping this file ~200 LOC
 * even with Phase 72.B documents added.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Plus, Pencil, Trash2, FileText, CalendarRange, StickyNote, History, Paperclip, GraduationCap } from 'lucide-react';

import AcademicProfileModal from './academic/AcademicProfileModal';
import AcademicReportsPanel from './academic/AcademicReportsPanel';
import AcademicAttendancePanel from './academic/AcademicAttendancePanel';
import AcademicNotesPanel from './academic/AcademicNotesPanel';
import AcademicTimelinePanel from './academic/AcademicTimelinePanel';
import AcademicDocumentsPanel from './academic/AcademicDocumentsPanel';
import { STATUS_COLORS, profileLabel } from './academic/constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const blankProfile = () => ({
  profile_name: '', school_or_provider_name: '', programme_or_course_name: '',
  academic_system: 'Portuguese', current_grade_or_year: '', status: 'Good',
  responsible_teacher_or_tutor: '',
  school_name: '', program_name: '', academic_year: '',
  start_date: '', expected_end_date: '', notes: '',
  // Phase 75.1 — optional course completion % tracking.
  track_course_completion: false,
});

const AcademicMultiProfileView = ({ athlete, canEdit }) => {
  const [profiles, setProfiles] = useState([]);
  const [allReports, setAllReports] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [notes, setNotes] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [activePanel, setActivePanel] = useState('reports'); // 'reports' | 'attendance' | 'notes' | 'documents'
  const [loading, setLoading] = useState(false);
  const [profileDlg, setProfileDlg] = useState({ open: false, editing: null, data: blankProfile() });

  // ─── fetchers ──────────────────────────────────────────────
  const fetchProfiles = async () => {
    if (!athlete?.id) return;
    setLoading(true);
    try {
      const [pr, rr] = await Promise.all([
        axios.get(`${API}/academic/profiles?athlete_id=${athlete.id}`),
        axios.get(`${API}/academic-reports/athlete/${athlete.id}`),
      ]);
      setProfiles(pr.data || []);
      setAllReports(rr.data || []);
      const hasUnassigned = (rr.data || []).some(r => !r.academic_profile_id);
      if ((pr.data || []).length > 0) {
        setActiveTab(t => t && [...pr.data.map(x => x.id), 'unassigned', 'timeline'].includes(t) ? t : pr.data[0].id);
      } else if (hasUnassigned) {
        setActiveTab('unassigned');
      } else {
        setActiveTab(null);
      }
    } catch { toast.error('Failed to load academic profiles'); }
    finally { setLoading(false); }
  };

  const fetchSidePanels = async (profileId) => {
    if (!athlete?.id || !profileId || profileId === 'unassigned' || profileId === 'timeline') {
      setAttendance([]); setNotes([]); return;
    }
    try {
      const [a, n] = await Promise.all([
        axios.get(`${API}/academic/attendance?athlete_id=${athlete.id}&academic_profile_id=${profileId}`),
        axios.get(`${API}/academic/notes?athlete_id=${athlete.id}&academic_profile_id=${profileId}`),
      ]);
      setAttendance(a.data || []);
      setNotes(n.data || []);
    } catch { /* ignore */ }
  };

  const fetchTimeline = async () => {
    if (!athlete?.id) return;
    try {
      const r = await axios.get(`${API}/academic/timeline?athlete_id=${athlete.id}`);
      setTimeline(r.data?.events || []);
    } catch { setTimeline([]); }
  };

  useEffect(() => { fetchProfiles(); /* eslint-disable-next-line */ }, [athlete?.id]);
  useEffect(() => {
    if (activeTab && activeTab !== 'unassigned' && activeTab !== 'timeline') fetchSidePanels(activeTab);
    if (activeTab === 'timeline') fetchTimeline();
    /* eslint-disable-next-line */
  }, [activeTab]);

  // ─── derived ───────────────────────────────────────────────
  const unassignedReports = useMemo(() => allReports.filter(r => !r.academic_profile_id), [allReports]);
  const reportsForActive = useMemo(() => {
    if (activeTab === 'unassigned') return unassignedReports;
    if (!activeTab || activeTab === 'timeline') return [];
    return allReports.filter(r => r.academic_profile_id === activeTab);
  }, [allReports, activeTab, unassignedReports]);
  const activeProfile = useMemo(() => profiles.find(p => p.id === activeTab) || null, [profiles, activeTab]);

  // ─── profile mutations ─────────────────────────────────────
  const submitProfile = async () => {
    const d = profileDlg.data;
    if (!d.profile_name && !d.school_name && !d.school_or_provider_name) {
      toast.error('A profile needs at least a profile name or school name'); return;
    }
    const payload = {
      ...d,
      school_name: d.school_name || d.school_or_provider_name || d.profile_name,
      academic_year: d.academic_year || d.current_grade_or_year || '2025/26',
      start_date: d.start_date || undefined,
      expected_end_date: d.expected_end_date || undefined,
    };
    try {
      if (profileDlg.editing) {
        await axios.put(`${API}/academic/profiles/${profileDlg.editing.id}`, payload);
        toast.success('Academic profile updated');
      } else {
        const res = await axios.post(`${API}/academic/profiles`, { athlete_id: athlete.id, ...payload });
        setActiveTab(res.data?.id || null);
        toast.success('Academic profile created');
      }
      setProfileDlg({ open: false, editing: null, data: blankProfile() });
      fetchProfiles();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save profile'); }
  };

  const removeProfile = async (id) => {
    if (!window.confirm('Delete this academic profile? Linked reports/attendance/notes will remain but become unassigned.')) return;
    try {
      await axios.delete(`${API}/academic/profiles/${id}`);
      toast.success('Academic profile deleted');
      if (activeTab === id) setActiveTab(null);
      fetchProfiles();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  if (!athlete?.id) return null;

  const renderTabButton = (key, label, isActive, onClick, badge) => (
    <button key={key} onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition whitespace-nowrap ${
        isActive ? 'bg-emerald-600 text-white border-emerald-600'
                 : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
      }`}
      data-testid={`academic-tab-${key}`}>
      {label}{badge !== undefined ? <span className="ml-1 opacity-75">({badge})</span> : null}
    </button>
  );

  return (
    <div className="space-y-4">
      {/* Profile tabs */}
      <Card data-testid="academic-profiles-tabs">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <GraduationCap className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <CardTitle className="text-base">Academic Profiles for {athlete.full_name}</CardTitle>
            </div>
            {canEdit && (
              <Button size="sm" onClick={() => setProfileDlg({ open: true, editing: null, data: blankProfile() })} data-testid="add-profile-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Profile
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-gray-500 text-center py-4">Loading…</p>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                {profiles.map(p => renderTabButton(p.id, profileLabel(p), activeTab === p.id, () => setActiveTab(p.id)))}
                {unassignedReports.length > 0 && renderTabButton('unassigned', 'General / Unassigned', activeTab === 'unassigned', () => setActiveTab('unassigned'), unassignedReports.length)}
                {(profiles.length > 0 || unassignedReports.length > 0) && renderTabButton('timeline', 'Timeline', activeTab === 'timeline', () => setActiveTab('timeline'))}
              </div>
              {profiles.length === 0 && unassignedReports.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-6" data-testid="academic-empty-state">No academic profile added yet.</p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Active profile panel */}
      {activeProfile && (
        <Card data-testid={`academic-profile-panel-${activeProfile.id}`}>
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="min-w-0">
                <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                  {profileLabel(activeProfile)}
                  <Badge className={STATUS_COLORS[activeProfile.status] || STATUS_COLORS.Good} data-testid="active-profile-status">
                    {activeProfile.status || 'Good'}
                  </Badge>
                </CardTitle>
                <p className="text-xs text-gray-500 mt-1">
                  {[activeProfile.academic_system, activeProfile.current_grade_or_year, activeProfile.school_or_provider_name || activeProfile.school_name].filter(Boolean).join(' · ')}
                </p>
                {activeProfile.responsible_teacher_or_tutor && (
                  <p className="text-xs text-gray-500">Teacher / Tutor: {activeProfile.responsible_teacher_or_tutor}</p>
                )}
              </div>
              {canEdit && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setProfileDlg({ open: true, editing: activeProfile, data: { ...blankProfile(), ...activeProfile, start_date: activeProfile.start_date || '', expected_end_date: activeProfile.expected_end_date || '' } })} data-testid="edit-active-profile-btn">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => removeProfile(activeProfile.id)} data-testid="delete-active-profile-btn">
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 mb-3 border-b border-gray-200 dark:border-gray-700 pb-2 flex-wrap">
              {[
                ['reports', 'Programme Reports & Grades', FileText, reportsForActive.length],
                ['attendance', 'Weekly Attendance', CalendarRange, attendance.length],
                ['notes', 'Notes', StickyNote, notes.length],
                ['documents', 'Documents', Paperclip, null],
              ].map(([key, label, Icon, count]) => (
                <button key={key} onClick={() => setActivePanel(key)}
                  className={`text-xs font-medium px-2.5 py-1 rounded transition ${
                    activePanel === key ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                  data-testid={`academic-panel-${key}`}>
                  <Icon className="w-3.5 h-3.5 inline mr-1" />
                  {label}{count !== null ? <span className="opacity-60"> ({count})</span> : null}
                </button>
              ))}
            </div>
            {activePanel === 'reports' && (
              <AcademicReportsPanel
                reports={reportsForActive}
                athlete={athlete}
                profile={activeProfile}
                canEdit={canEdit}
                onChanged={fetchProfiles}
              />
            )}
            {activePanel === 'attendance' && (
              <AcademicAttendancePanel athlete={athlete} profile={activeProfile} canEdit={canEdit} attendance={attendance} onChanged={() => fetchSidePanels(activeProfile.id)} />
            )}
            {activePanel === 'notes' && (
              <AcademicNotesPanel athlete={athlete} profile={activeProfile} canEdit={canEdit} items={notes} onChanged={() => fetchSidePanels(activeProfile.id)} />
            )}
            {activePanel === 'documents' && (
              <AcademicDocumentsPanel athlete={athlete} profile={activeProfile} canEdit={canEdit} />
            )}
          </CardContent>
        </Card>
      )}

      {/* Unassigned bucket */}
      {activeTab === 'unassigned' && (
        <Card data-testid="academic-unassigned-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">General / Unassigned Reports</CardTitle>
            <p className="text-xs text-gray-500">Older reports created before academic profiles were introduced.</p>
          </CardHeader>
          <CardContent><AcademicReportsPanel reports={unassignedReports} /></CardContent>
        </Card>
      )}

      {/* Timeline */}
      {activeTab === 'timeline' && (
        <Card data-testid="academic-timeline-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4" /> Academic Timeline</CardTitle>
            <p className="text-xs text-gray-500">Derived from the System Audit Log.</p>
          </CardHeader>
          <CardContent><AcademicTimelinePanel events={timeline} /></CardContent>
        </Card>
      )}

      <AcademicProfileModal state={profileDlg} setState={setProfileDlg} onSave={submitProfile} />
    </div>
  );
};

export default AcademicMultiProfileView;
